import {
	defaultRangeExtractor,
	useWindowVirtualizer,
	type Range,
} from '@tanstack/react-virtual'
import { Edit05, Stars01 } from '#app/components/icons/duotone'
import { ChevronRight } from '#app/components/icons/solid'
import split from 'just-split'
import { useCallback, useMemo, useRef, useState } from 'react'
import { Fragment } from 'react/jsx-runtime'
import { IconCard } from '#app/components/icon-card'
import { getFilteredIcons } from '#app/utils/get-filtered-icons'
import { getSmartFilteredIcons } from '#app/utils/get-smart-filtered-icons'
import { type getSsrIcons } from '#app/utils/get-ssr-icons'
import {
	cn,
	reconstructIconPath,
	type DirectoryWithIcons,
	type DirectoriesWithIconsBySearch,
	DIRECTORY_HEADER_HEIGHT,
	ICON_CARD_GRID_COLUMN_GAP,
	ICON_CARD_GRID_ROW_GAP,
	ICON_CARD_MIN_WIDTH,
	ICON_CARD_NAME_HEIGHT,
	ICON_CARD_GAP,
	formatCount,
} from '#app/utils/misc.js'
import {
	type IconFormat,
	type SearchType,
	type IconName,
} from '#app/utils/search-params'
import { LoadingIndicator } from './application/loading-indicator/loading-indicator'
import { EditIconSetModal } from './application/modals/edit-icon-set-modal'
import { IconGroupLicensePopover } from './icon-group-license-popover'
import { Button } from './shared/buttons/button'

type UploadedSetInfo = { id: string; name: string; slug: string; count: number }
type PublicIconSetInfo = {
	name: string
	slug: string
	author: string | null
	authorUrl: string | null
	license: string | null
}

export const IconGridBySearch = ({
	stickyHeaderHeight,
	directoriesWithIcons,
	scrollMarginRef,
	width,
	query,
	hasMoreIcons = false,
	uploadedId,
	uploadedSetsInfo,
	publicIconSetsInfo,
	smartResults,
	hasMoreSmartResults = false,
	searchType,
	onSearchTypeChange,
	isSmartSearchReady,
	format,
	iconName,
}: {
	stickyHeaderHeight: number
	directoriesWithIcons: DirectoryWithIcons[]
	scrollMarginRef: React.RefObject<HTMLDivElement | null>
	width: number
	query: string
	hasMoreIcons?: boolean
	uploadedId?: string
	uploadedSetsInfo?: UploadedSetInfo[]
	publicIconSetsInfo?: PublicIconSetInfo[]
	smartResults?: Record<string, string[]>
	hasMoreSmartResults?: boolean
	searchType?: SearchType
	onSearchTypeChange?: (s: SearchType) => void
	isSmartSearchReady?: boolean
	format: IconFormat
	iconName?: IconName
}) => {
	if (searchType === 'smart' && query.length >= 3) {
		if (!smartResults) {
			return (
				<div className="py-10">
					<LoadingIndicator type="line-simple" size="xl" label="Searching..." />
				</div>
			)
		}

		const directoriesWithIconsBySearch = getSmartFilteredIcons(
			directoriesWithIcons,
			smartResults,
		)

		if (directoriesWithIconsBySearch.length === 0) {
			return hasMoreSmartResults ? (
				<div className="py-10">
					<LoadingIndicator type="line-simple" size="xl" label="Searching..." />
				</div>
			) : (
				<NoIconsFound
					query={query}
					searchType={searchType}
					onSearchTypeChange={onSearchTypeChange}
					isSmartSearchReady={isSmartSearchReady}
				/>
			)
		}

		return (
			<>
				<IconGridInner
					directoriesWithIconsBySearch={directoriesWithIconsBySearch}
					scrollMarginRef={scrollMarginRef}
					stickyHeaderHeight={stickyHeaderHeight}
					width={width}
					uploadedId={uploadedId}
					uploadedSetsInfo={uploadedSetsInfo}
					publicIconSetsInfo={publicIconSetsInfo}
					format={format}
					iconName={iconName}
				/>
				{hasMoreSmartResults && (
					<div className="py-10">
						<LoadingIndicator
							type="line-simple"
							size="xl"
							label="Searching..."
						/>
					</div>
				)}
			</>
		)
	}

	const directoriesWithIconsBySearch = getFilteredIcons(
		directoriesWithIcons,
		query,
	)

	if (directoriesWithIconsBySearch.length === 0) {
		return (
			<NoIconsFound
				query={query}
				searchType={searchType}
				onSearchTypeChange={onSearchTypeChange}
				isSmartSearchReady={isSmartSearchReady}
			/>
		)
	}

	return (
		<>
			<IconGridInner
				directoriesWithIconsBySearch={directoriesWithIconsBySearch}
				scrollMarginRef={scrollMarginRef}
				stickyHeaderHeight={stickyHeaderHeight}
				width={width}
				uploadedId={uploadedId}
				uploadedSetsInfo={uploadedSetsInfo}
				publicIconSetsInfo={publicIconSetsInfo}
				format={format}
				iconName={iconName}
			/>
			{hasMoreIcons && (
				<div className="py-10">
					<LoadingIndicator
						type="line-simple"
						size="xl"
						label="Loading icons..."
					/>
				</div>
			)}
		</>
	)
}

export const IconGridServer = ({
	directoriesWithIcons,
	stickyHeaderHeight,
	query,
	hasMoreIcons = true,
	uploadedId,
	publicIconSetsInfo,
	searchType,
	onSearchTypeChange,
	isSmartSearchReady,
	format,
	iconName,
}: {
	directoriesWithIcons: ReturnType<typeof getSsrIcons>
	stickyHeaderHeight: number
	query: string
	hasMoreIcons?: boolean
	uploadedId?: string
	publicIconSetsInfo?: PublicIconSetInfo[]
	searchType?: SearchType
	onSearchTypeChange?: (s: SearchType) => void
	isSmartSearchReady?: boolean
	format: IconFormat
	iconName?: IconName
}) => {
	if (directoriesWithIcons.length === 0) {
		return (
			<NoIconsFound
				query={query}
				searchType={searchType}
				onSearchTypeChange={onSearchTypeChange}
				isSmartSearchReady={isSmartSearchReady}
			/>
		)
	}

	return (
		<div>
			<div
				className="grid h-full"
				style={{
					gridTemplateColumns: `repeat(auto-fill,minmax(${ICON_CARD_MIN_WIDTH}px,1fr))`,
					columnGap: `${ICON_CARD_GRID_COLUMN_GAP}px`,
				}}
			>
				{directoriesWithIcons.map((directory) => (
					<Fragment key={directory.path}>
						<div
							className="bg-primary sticky top-0 z-10 col-span-full"
							style={{
								top: `${stickyHeaderHeight}px`,
								height: `${DIRECTORY_HEADER_HEIGHT}px`,
							}}
						>
							<DirectoryRow
								breadcrumbs={directory.breadcrumbs}
								iconCount={directory.actualIconCount}
								uploadedId={uploadedId}
								publicIconSetsInfo={publicIconSetsInfo}
							/>
						</div>
						{directory.icons.map((icon) => (
							<div
								data-key={reconstructIconPath({
									path: icon.path,
									name: icon.name,
								})}
								key={reconstructIconPath({
									path: icon.path,
									name: icon.name,
								})}
								style={{
									paddingBottom: `${ICON_CARD_GRID_ROW_GAP}px`,
								}}
							>
								<IconCard
									icon={{
										id: icon.id,
										name: icon.name,
										svg: icon.svg,
										color: icon.color,
										strokeWidth: icon.strokeWidth,
										originalIconId: uploadedId ? icon.id : undefined,
									}}
									format={format}
									showIconName={iconName === 'true'}
								/>
							</div>
						))}
					</Fragment>
				))}
			</div>
			{hasMoreIcons && (
				<div className="py-10">
					<LoadingIndicator
						type="line-simple"
						size="xl"
						label="Loading icons..."
					/>
				</div>
			)}
		</div>
	)
}

export const IconGridInner = ({
	directoriesWithIconsBySearch,
	scrollMarginRef,
	stickyHeaderHeight,
	width,
	uploadedId,
	uploadedSetsInfo,
	publicIconSetsInfo,
	format,
	iconName,
}: {
	directoriesWithIconsBySearch: DirectoriesWithIconsBySearch
	scrollMarginRef: React.RefObject<HTMLDivElement | null>
	stickyHeaderHeight: number
	width: number
	uploadedId?: string
	uploadedSetsInfo?: UploadedSetInfo[]
	publicIconSetsInfo?: PublicIconSetInfo[]
	format: IconFormat
	iconName?: IconName
}) => {
	const iconsPerRowCount = Math.floor(
		(width + ICON_CARD_GRID_COLUMN_GAP) /
			(ICON_CARD_MIN_WIDTH + ICON_CARD_GRID_COLUMN_GAP),
	)

	const iconWidth =
		(width - (iconsPerRowCount - 1) * ICON_CARD_GRID_COLUMN_GAP) /
		iconsPerRowCount

	const iconRowsCount = directoriesWithIconsBySearch.reduce(
		(rowsCount, directoryWithIcons) =>
			rowsCount + Math.ceil(directoryWithIcons.icons.length / iconsPerRowCount),
		0,
	)

	const rowsOfDirecotryOrIcons = directoriesWithIconsBySearch.reduce(
		(rows, directortyWithIcons) => {
			const rowsOfIcons = split(directortyWithIcons.icons, iconsPerRowCount)
			return [
				...rows,
				{
					type: 'directory' as const,
					directoryPath: directortyWithIcons.path,
					breadcrumbs: directortyWithIcons.breadcrumbs,
					iconCount: directortyWithIcons.icons.length,
				},
				...rowsOfIcons.map((icons) => ({
					type: 'icons' as const,
					directoryPath: directortyWithIcons.path,
					icons,
				})),
			]
		},
		[] as Array<
			| {
					type: 'directory'
					iconCount: number
					directoryPath: string
					breadcrumbs: string[]
			  }
			| {
					type: 'icons'
					directoryPath: string
					icons: (typeof directoriesWithIconsBySearch)[0]['icons']
			  }
		>,
	)

	const activeStickyIndexRef = useRef(0)
	const stickyIndexes = useMemo(
		() =>
			directoriesWithIconsBySearch.map((directory) =>
				rowsOfDirecotryOrIcons.findIndex(
					(row) =>
						row.type === 'directory' && row.directoryPath === directory.path,
				),
			),
		[rowsOfDirecotryOrIcons, directoriesWithIconsBySearch],
	)
	const isSticky = (index: number) => stickyIndexes.includes(index)
	const isActiveSticky = (index: number) =>
		activeStickyIndexRef.current === index

	const nameHeight =
		iconName === 'false' ? 0 : ICON_CARD_NAME_HEIGHT + ICON_CARD_GAP
	const iconHeight = nameHeight + iconWidth + ICON_CARD_GRID_ROW_GAP
	const virtualizedItemsCount =
		directoriesWithIconsBySearch.length + iconRowsCount
	const rowVirtualizer = useWindowVirtualizer({
		count: virtualizedItemsCount,
		estimateSize: useCallback(
			(index) =>
				rowsOfDirecotryOrIcons[index]?.type === 'directory'
					? DIRECTORY_HEADER_HEIGHT
					: iconHeight,
			[iconHeight, rowsOfDirecotryOrIcons],
		),
		overscan: 5,
		getItemKey: useCallback(
			(index: number) => {
				if (rowsOfDirecotryOrIcons[index]?.type === 'directory') {
					return `directory-${rowsOfDirecotryOrIcons[index].directoryPath}`
				} else {
					if (!rowsOfDirecotryOrIcons[index]) {
						throw new Error('Invalid getItemKey index')
					}
					return `icons-${rowsOfDirecotryOrIcons[index].directoryPath}-${rowsOfDirecotryOrIcons[
						index
					].icons
						.map((icon) =>
							reconstructIconPath({ path: icon.path, name: icon.name }),
						)
						.join(',')}`
				}
			},
			[rowsOfDirecotryOrIcons],
		),
		rangeExtractor: useCallback(
			(range: Range) => {
				activeStickyIndexRef.current =
					[...stickyIndexes]
						.reverse()
						.find((index) => range.startIndex >= index) ?? 0

				const next = new Set([
					activeStickyIndexRef.current,
					...defaultRangeExtractor(range),
				])

				return [...next].sort((a, b) => a - b)
			},
			[stickyIndexes],
		),
		// the static area above sticky bits e.g. above search bar
		scrollMargin: scrollMarginRef.current
			? scrollMarginRef.current.getBoundingClientRect().top +
				window.scrollY -
				stickyHeaderHeight
			: 0,
	})

	return (
		<div
			className="relative mb-2.5"
			style={{
				height: `${rowVirtualizer.getTotalSize()}px`,
			}}
		>
			{rowVirtualizer.getVirtualItems().map((virtualRow) => {
				const rowOfDirecotryOrIcons = rowsOfDirecotryOrIcons[virtualRow.index]
				return rowOfDirecotryOrIcons ? (
					<div
						key={virtualRow.key}
						style={{
							left: 0,
							width: '100%',
							height: `${virtualRow.size}px`,
							...(isActiveSticky(virtualRow.index)
								? {
										position: 'sticky',
										top: `${stickyHeaderHeight}px`,
									}
								: {
										position: 'absolute',
										top: 0,
										transform: `translateY(${virtualRow.start - rowVirtualizer.options.scrollMargin}px)`,
									}),
						}}
						className={cn(isSticky(virtualRow.index) ? 'bg-primary z-10' : '')}
					>
						{rowOfDirecotryOrIcons.type === 'directory' ? (
							<DirectoryRow
								breadcrumbs={rowOfDirecotryOrIcons.breadcrumbs}
								iconCount={rowOfDirecotryOrIcons.iconCount}
								uploadedId={uploadedId}
								uploadedSetsInfo={uploadedSetsInfo}
								publicIconSetsInfo={publicIconSetsInfo}
							/>
						) : (
							<div
								className="grid h-full"
								style={{
									gridTemplateColumns: `repeat(auto-fill,minmax(${ICON_CARD_MIN_WIDTH}px,1fr))`,
									columnGap: `${ICON_CARD_GRID_COLUMN_GAP}px`,
								}}
							>
								{rowOfDirecotryOrIcons.icons.map((icon) => (
									<IconCard
										key={reconstructIconPath({
											path: icon.path,
											name: icon.name,
										})}
										icon={{
											id: icon.id,
											name: icon.name,
											svg: icon.svg,
											color: icon.color,
											strokeWidth: icon.strokeWidth,
											originalIconId: uploadedId ? icon.id : undefined,
										}}
										format={format}
										showIconName={iconName === 'true'}
									/>
								))}
							</div>
						)}
					</div>
				) : null
			})}
		</div>
	)
}

const DirectoryRow = ({
	breadcrumbs,
	iconCount,
	uploadedId,
	uploadedSetsInfo,
	publicIconSetsInfo,
}: {
	breadcrumbs: string[]
	iconCount: number
	uploadedId?: string
	uploadedSetsInfo?: UploadedSetInfo[]
	publicIconSetsInfo?: PublicIconSetInfo[]
}) => {
	const [isEditModalOpen, setIsEditModalOpen] = useState(false)
	const iconSetName = breadcrumbs[0]
	const resolvedUploadedId =
		uploadedId ?? uploadedSetsInfo?.find((s) => s.name === iconSetName)?.id
	const hasEditButton = resolvedUploadedId && iconSetName

	// Find public icon set info for license popover (match by slug since breadcrumbs are kebab-cased)
	const publicIconSetInfo = publicIconSetsInfo?.find(
		(s) => s.slug === iconSetName,
	)
	const hasLicenseInfo =
		publicIconSetInfo && (publicIconSetInfo.author || publicIconSetInfo.license)

	return (
		<div className="flex h-full items-center justify-between gap-2">
			<div className="flex flex-wrap items-center gap-2">
				{hasEditButton ? (
					<>
						<Button
							size="sm"
							color="tertiary"
							iconLeading={Edit05}
							onClick={() => setIsEditModalOpen(true)}
						>
							{iconSetName}
						</Button>
						<EditIconSetModal
							uploadedId={resolvedUploadedId}
							initialName={iconSetName}
							isOpen={isEditModalOpen}
							onOpenChange={setIsEditModalOpen}
						/>
					</>
				) : hasLicenseInfo && iconSetName ? (
					<>
						<IconGroupLicensePopover
							name={iconSetName}
							author={publicIconSetInfo.author}
							authorUrl={publicIconSetInfo.authorUrl}
							license={publicIconSetInfo.license}
						/>
						{breadcrumbs.slice(1).map((part) => (
							<Fragment key={part}>
								<ChevronRight className="text-fg-quaternary size-4 shrink-0" />
								<span className="text-secondary text-sm font-semibold">
									{part}
								</span>
							</Fragment>
						))}
					</>
				) : breadcrumbs.length === 0 ? (
					<span className="text-secondary text-sm font-semibold">All</span>
				) : (
					breadcrumbs.map((part, i) => {
						return (
							<Fragment key={part}>
								<span className="text-secondary text-sm font-semibold">
									{part}
								</span>

								{i < breadcrumbs.length - 1 ? (
									<ChevronRight className="text-fg-quaternary size-4 shrink-0"></ChevronRight>
								) : null}
							</Fragment>
						)
					})
				)}
				{hasEditButton && (
					<>
						{breadcrumbs.slice(1).map((part) => (
							<Fragment key={part}>
								<ChevronRight className="text-fg-quaternary size-4 shrink-0" />
								<span className="text-secondary text-sm font-semibold">
									{part}
								</span>
							</Fragment>
						))}
					</>
				)}
			</div>
			<div className="text-secondary text-sm font-semibold">
				{formatCount(iconCount)} icons
			</div>
		</div>
	)
}

const NoIconsFound = ({
	query,
	searchType,
	onSearchTypeChange,
	isSmartSearchReady,
}: {
	query: string
	searchType?: SearchType
	onSearchTypeChange?: (s: SearchType) => void
	isSmartSearchReady?: boolean
}) => {
	const showSuggestion =
		searchType === 'exact' && isSmartSearchReady && onSearchTypeChange

	return (
		<div className="flex flex-col items-start gap-2">
			<p className="text-secondary flex h-12 items-center text-sm font-semibold">
				No icons found for "{query}"
			</p>
			{showSuggestion && (
				<Button
					color="secondary"
					iconLeading={Stars01}
					onClick={() => onSearchTypeChange('smart')}
				>
					Try Smart Search
				</Button>
			)}
		</div>
	)
}
